import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { getFinanceItems } from '../api/figApi';
import { CASH_MASKS, INVEST_MASKS, latestBalances, accountName, liquidCash, cashBalance } from './accounts';
import { classifySource, isSelfPayroll, normalizeMerchant } from './titleRules';
import { maskByAccountId } from './netWorth';
import { liveTransactions, isPending, pendingByMask } from './pending';
import { matchRecurring, variableSpend, forwardRunway, housingOutlook, localDay, RUNWAY_MAX_MONTHS } from './recurring';
import { fmtBal, fmtDay, fmtDayShort } from './format';
import NetWorthTab from './NetWorthTab';
import RecurringTab from './RecurringTab';
import ReceiptChip from './receipts/ReceiptChip';
import ReceiptsView from './receipts/ReceiptsView';
import './Finance.css';

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmt(n) {
  return '$' + Math.round(Math.abs(n)).toLocaleString();
}

// Signed dollar delta, no arrow glyph — color alone conveys direction.
function fmtDeltaNoArrow(current, baseline) {
  if (baseline === undefined) return <span style={{ color: '#bbb' }}>—</span>;
  const d = Math.round(current - baseline);
  if (d > 0) return <span style={{ color: '#c0392b' }}>+${d.toLocaleString()}</span>;
  if (d < 0) return <span style={{ color: '#27ae60' }}>−${Math.abs(d).toLocaleString()}</span>;
  return <span style={{ color: '#888' }}>$0</span>;
}

// Prior-period total as a single dollar amount, colored by current-vs-baseline pace (no arrows).
// Shared by "vs same pd" (prior same-day MTD) and "vs last mo" (prior full month).
function fmtSamePeriod(current, baseline) {
  if (baseline === undefined) return <span style={{ color: '#bbb' }}>—</span>;
  const amt = `$${Math.round(baseline).toLocaleString()}`;
  let color = '#888';
  if (current < baseline) color = '#27ae60';      // under baseline pace = green
  else if (current > baseline) color = '#c0392b'; // over = red
  return <span style={{ color }}>{amt}</span>;
}

function fmtMonth(yyyymm) {
  const [y, m] = yyyymm.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

function lerpColor(a, b, t) {
  const ah = parseInt(a.slice(1), 16);
  const ar = (ah >> 16) & 0xff, ag = (ah >> 8) & 0xff, ab = ah & 0xff;
  const bh = parseInt(b.slice(1), 16);
  const br = (bh >> 16) & 0xff, bg = (bh >> 8) & 0xff, bb = bh & 0xff;
  return `rgb(${Math.round(ar + (br - ar) * t)},${Math.round(ag + (bg - ag) * t)},${Math.round(ab + (bb - ab) * t)})`;
}

function percentile(vals, p) {
  const a = vals.filter(v => v > 0).sort((x, y) => x - y);
  if (!a.length) return 1;
  const idx = Math.min(Math.ceil(p * a.length) - 1, a.length - 1);
  return Math.max(a[idx], 1);
}

// Trust is the client-funds account: it has its own tab and is never part of "All".
const CONTEXTS = ['All', 'Personal', 'FIG', 'Monduras', 'Trust'];
const CONTEXT_LABELS = { Trust: 'Trust (client funds)' };

// Not a context: a balance-sheet view (what is owned today), where the tabs
// before it are cash-flow lenses (what moved in a month). It sits after a
// divider in the tab row and has no month selector.
const NET_WORTH = 'Net Worth';

// Not a context either: the bills expected to go out, whatever the month. It
// has its own All / Personal / FIG / Monduras toggle (never Trust) and no month
// selector. Last tab before the divider.
const RECURRING = 'Recurring';

// SVG chart constants
const LABEL_W   = 182;
const BAR_MAX_W = 230;
const AMT_W     = 68;
const BAR_H     = 28;
const BAR_GAP   = 7;

// Monthly earn/spend trend
const M_BAR       = 27;
const M_INNER_GAP = 4;
const M_PAIR_W    = M_BAR * 2 + M_INNER_GAP;
const M_OUTER_GAP = 16;
const M_SLOT      = M_PAIR_W + M_OUTER_GAP;
const M_PAD_L     = 10;
const M_H         = 120;
const M_PAD_T     = 24;
const M_SVG_H     = M_PAD_T + M_H + 38;
const M_PAGE      = 6;

// Quarterly average trend
const Q_BAR       = 40;
const Q_INNER_GAP = 6;
const Q_PAIR_W    = Q_BAR * 2 + Q_INNER_GAP;
const Q_OUTER_GAP = 24;
const Q_SLOT      = Q_PAIR_W + Q_OUTER_GAP;
const Q_PAD_L     = 12;
const Q_H         = 100;
const Q_PAD_T     = 24;
const Q_SVG_H     = Q_PAD_T + Q_H + 38;

// ── Component ─────────────────────────────────────────────────────────────────

export default function Finance() {
  const { instance, accounts } = useMsal();

  const [transactions,    setTransactions]    = useState([]);
  const [categoryGroupMap, setCategoryGroupMap] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selectedContext, setSelectedContext] = useState('All');
  const [selectedMonth,   setSelectedMonth]   = useState('');
  const [monthlyScaleMode, setMonthlyScaleMode] = useState('linear');
  const [qtrScaleMode,     setQtrScaleMode]     = useState('linear');
  const [monthlyWindowStart, setMonthlyWindowStart] = useState(Number.MAX_SAFE_INTEGER);
  const [expandedCat, setExpandedCat] = useState(null);
  // Receipts: null = the Finance page; else the view it opened on
  // ({ kind: 'dash' } or { kind: 'receipt', id }).
  const [receiptsView, setReceiptsView] = useState(null);
  const [balances,        setBalances]        = useState(null);   // null = still loading
  const [balancesError,   setBalancesError]   = useState(null);
  const [netWorthData,    setNetWorthData]    = useState(null);   // null = not loaded yet
  const [netWorthError,   setNetWorthError]   = useState(null);
  const [recurring,       setRecurring]       = useState(null);   // null = still loading
  const [recurringError,  setRecurringError]  = useState(null);

  // ── Auth (same pattern as Centcom.js) ───────────────────────────────────────
  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const [cats, txs] = await Promise.all([
        getFinanceItems(token, 'CATEGORIES'),
        getFinanceItems(token, 'TRANSACTIONS'),
      ]);

      const map = {};
      for (const c of cats) {
        if (c.id && c.Title) map[c.id] = { name: c.Title, group: c.Group };
      }
      setCategoryGroupMap(map);
      // Rows Plaid withdrew (Notes PLAID_REMOVED) are dropped here, so no
      // total, list, Runway or recurring match ever sees them.
      const live = liveTransactions(txs);
      setTransactions(live);

      const months = [...new Set(live.map(t => t.Date?.slice(0, 7)).filter(Boolean))].sort();
      if (months.length > 0) setSelectedMonth(months[months.length - 1]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Balances load on their own so a problem with Finance_Balances costs the
  // accounts header only, never the rest of the page.
  const fetchBalances = useCallback(async () => {
    try {
      setBalancesError(null);
      setBalances(null);
      const token = await getToken();
      setBalances(await getFinanceItems(token, 'BALANCES'));
    } catch (err) {
      setBalancesError(err.message);
    }
  }, [getToken]);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  // Finance_Accounts is read once per visit and shared: Recurring (account
  // masks its items match on) and Net Worth both wait on the same request.
  const accountsRef = useRef(null);
  const loadAccounts = useCallback(() => {
    if (!accountsRef.current) {
      accountsRef.current = getToken()
        .then(token => getFinanceItems(token, 'ACCOUNTS'))
        .catch(err => { accountsRef.current = null; throw err; });
    }
    return accountsRef.current;
  }, [getToken]);

  // Finance_Recurring (with Finance_Accounts, for the account masks its items
  // match on) loads on its own too: Runway and the Recurring tab need it, the
  // cash-flow tabs do not. Reads only — the page writes nothing to this list.
  const fetchRecurring = useCallback(async () => {
    try {
      setRecurringError(null);
      setRecurring(null);
      const token = await getToken();
      const [items, accts] = await Promise.all([
        getFinanceItems(token, 'RECURRING'),
        loadAccounts(),
      ]);
      setRecurring({ items, accounts: accts });
    } catch (err) {
      setRecurringError(err.message);
    }
  }, [getToken, loadAccounts]);

  useEffect(() => { fetchRecurring(); }, [fetchRecurring]);

  // The Net Worth lists load the first time that tab is opened, and on their
  // own: the cash-flow tabs never wait on them and never break because of them.
  // Reads only — the page writes nothing to these lists.
  const netWorthView  = selectedContext === NET_WORTH;
  const recurringView = selectedContext === RECURRING;
  const fetchNetWorth = useCallback(async () => {
    try {
      setNetWorthError(null);
      const token = await getToken();
      const [accts, assets, holdings, invTxns] = await Promise.all([
        loadAccounts(),
        getFinanceItems(token, 'ASSETS'),
        getFinanceItems(token, 'HOLDINGS'),
        getFinanceItems(token, 'INVESTMENT_TXNS'),
      ]);
      setNetWorthData({ accounts: accts, assets, holdings, invTxns });
    } catch (err) {
      setNetWorthError(err.message);
    }
  }, [getToken, loadAccounts]);

  const netWorthRequested = useRef(false);
  useEffect(() => {
    if (!netWorthView || netWorthRequested.current) return;
    netWorthRequested.current = true;
    fetchNetWorth();
  }, [netWorthView, fetchNetWorth]);

  // ── Derived values ────────────────────────────────────────────────────────────
  // A row's group decides whether it is real money or just money moving. The
  // category says so, with one exception: self-payroll (FIG's owner-wages debit
  // and the personal deposit it becomes) on the consolidated All view, where it
  // is Movement — Marc's entities paying each other. On FIG or Personal the
  // row's own category stands: a Business expense, an Inflow.
  const consolidatedView = selectedContext === 'All';
  const groupOf = (t, consolidated = consolidatedView) => {
    if (consolidated && isSelfPayroll(t)) return 'Movement';
    return categoryGroupMap[t.CategoryLookupId]?.group;
  };
  const catName = (t) => categoryGroupMap[t.CategoryLookupId]?.name || 'Uncategorized';
  const isMovement      = (t) => groupOf(t) === 'Movement';
  const isExcludedGroup = (t, consolidated = consolidatedView) => {
    const g = groupOf(t, consolidated);
    return g === 'Movement' || g === 'Assets & Savings';
  };
  const earnClass = (t) => String(t.CategoryLookupId) === '35' ? 'Commission' : classifySource(t.Title);
  const spendOk = (t) => t.Direction === 'Out' && !isExcludedGroup(t);

  const allMonths = [...new Set(transactions.map(t => t.Date?.slice(0, 7)).filter(Boolean))].sort();

  // Tab filter. "All" is everything EXCEPT Trust — client funds are not Marc's
  // money, so no Trust row (Movement or otherwise) reaches the All totals.
  const inContext = (t) => selectedContext === 'All' ? t.Context !== 'Trust' : t.Context === selectedContext;

  // Context + month filter
  const filtered = transactions.filter(t => {
    const mOk = !selectedMonth || t.Date?.startsWith(selectedMonth);
    return inContext(t) && mOk;
  });

  // Summary
  const realIn      = filtered.filter(t => t.Direction === 'In'  && !isExcludedGroup(t)).reduce((s, t) => s + (t.Amount ?? 0), 0);
  const realOut     = filtered.filter(spendOk).reduce((s, t) => s + (t.Amount ?? 0), 0);
  const net         = realIn - realOut;
  const movTotal    = filtered.filter(t => isMovement(t)).reduce((s, t) => s + (t.Amount ?? 0), 0);
  const assetsTotal = filtered.filter(t => groupOf(t) === 'Assets & Savings').reduce((s, t) => s + (t.Amount ?? 0), 0);

  // Spend by category — Out, excl Movement, sorted descending
  const catTotals = {};
  for (const t of filtered) {
    if (spendOk(t)) {
      const cat = catName(t);
      catTotals[cat] = (catTotals[cat] ?? 0) + (t.Amount ?? 0);
    }
  }
  const sortedCats = Object.entries(catTotals).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  const maxCat    = sortedCats.length > 0 ? Math.abs(sortedCats[0][1]) : 1;

  // Per-category delta baselines — only computed when a month is selected
  const buildMonthCat = (monthPrefix) => {
    const totals = {};
    for (const t of transactions) {
      if (inContext(t) && t.Date?.startsWith(monthPrefix) && spendOk(t)) {
        const cat = catName(t);
        totals[cat] = (totals[cat] ?? 0) + (t.Amount ?? 0);
      }
    }
    return totals;
  };

  // Same as buildMonthCat but only counts rows on/before a day-of-month cap (string-safe).
  const buildMonthCatThroughDay = (monthPrefix, dayCap) => {
    const totals = {};
    for (const t of transactions) {
      if (inContext(t) && t.Date?.startsWith(monthPrefix) && spendOk(t)) {
        const day = parseInt(t.Date.slice(8, 10), 10);   // DD, string-safe — no new Date()
        if (Number.isFinite(day) && day <= dayCap) {
          const cat = catName(t);
          totals[cat] = (totals[cat] ?? 0) + (t.Amount ?? 0);
        }
      }
    }
    return totals;
  };

  // Is the viewed month the live, still-accumulating current month? (new Date() = TODAY, not a tx date)
  const now                        = new Date();
  const todayMonthPrefix           = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const todayDay                   = now.getDate();
  const viewingPartialCurrentMonth = selectedMonth === todayMonthPrefix;

  let prevMonthCatTotals  = {};
  let qtrAvgCatTotals     = {};
  let samePeriodCatTotals = null;

  if (selectedMonth) {
    const [yr, mo] = selectedMonth.split('-').map(Number);

    const prevMo = mo === 1 ? 12 : mo - 1;
    const prevYr = mo === 1 ? yr - 1 : yr;
    const prevPrefix = `${prevYr}-${String(prevMo).padStart(2, '0')}`;
    prevMonthCatTotals = buildMonthCat(prevPrefix);

    // vs same period: prior month capped to the same day-of-month we've reached this month.
    // Only meaningful while viewing the live partial current month; otherwise stays null (blank column).
    if (viewingPartialCurrentMonth) {
      samePeriodCatTotals = buildMonthCatThroughDay(prevPrefix, todayDay);
    }

    const qtrSum = {}, qtrCount = {};
    for (let i = 0; i < 3; i++) {
      const raw = mo - i;
      const m   = raw <= 0 ? raw + 12 : raw;
      const y   = raw <= 0 ? yr - 1  : yr;
      for (const [cat, amt] of Object.entries(buildMonthCat(`${y}-${String(m).padStart(2, '0')}`))) {
        qtrSum[cat]   = (qtrSum[cat]   ?? 0) + amt;
        qtrCount[cat] = (qtrCount[cat] ?? 0) + 1;
      }
    }
    for (const cat of Object.keys(qtrSum)) qtrAvgCatTotals[cat] = qtrSum[cat] / qtrCount[cat];
  }

  // Monthly earn/spend — context-filtered, all months, NOT month-filtered
  const monthlyTrend = allMonths.map(month => {
    const txs = transactions.filter(t => inContext(t) && t.Date?.startsWith(month) && !isExcludedGroup(t));
    const inTxs          = txs.filter(t => t.Direction === 'In');
    const earnFIG        = inTxs.filter(t => earnClass(t) === 'FIG').reduce((s, t)        => s + (t.Amount ?? 0), 0);
    const earnFedEx      = inTxs.filter(t => earnClass(t) === 'FedEx').reduce((s, t)      => s + (t.Amount ?? 0), 0);
    const earnOther      = inTxs.filter(t => earnClass(t) === 'Other').reduce((s, t)      => s + (t.Amount ?? 0), 0);
    const earnCommission = inTxs.filter(t => earnClass(t) === 'Commission').reduce((s, t) => s + (t.Amount ?? 0), 0);
    const earn  = earnFIG + earnFedEx + earnOther + earnCommission;
    const spend = txs.filter(t => t.Direction === 'Out').reduce((s, t) => s + (t.Amount ?? 0), 0);
    return { month, earnFIG, earnFedEx, earnOther, earnCommission, earn, spend, net: earn - spend };
  });
  const maxMonthlyVal = monthlyScaleMode === 'linear'
    ? Math.max(...monthlyTrend.flatMap(d => [d.earn, d.spend]), 1)
    : percentile(monthlyTrend.flatMap(d => [d.earn, d.spend]), 0.75);
  const maxStart      = Math.max(0, monthlyTrend.length - M_PAGE);
  const windowStart   = Math.min(monthlyWindowStart, maxStart);
  const visibleMonths = monthlyTrend.slice(windowStart, windowStart + M_PAGE);
  const mSvgW         = M_PAD_L * 2 + M_PAGE * M_SLOT;

  // Quarterly average — keyed YYYY-Qn so Q1'26 and Q1'27 never collapse
  const quarterMap = {};
  for (const { month, earnFIG, earnFedEx, earnOther, earnCommission, earn, spend, net } of monthlyTrend) {
    const [y, mo] = month.split('-');
    const q = Math.ceil(parseInt(mo, 10) / 3);
    const key = `${y}-Q${q}`;
    if (!quarterMap[key]) quarterMap[key] = { year: y, q, months: [] };
    quarterMap[key].months.push({ earnFIG, earnFedEx, earnOther, earnCommission, earn, spend, net });
  }
  const quarterlyTrend = Object.entries(quarterMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, { year, q, months }]) => {
      const count             = months.length;
      const avgEarnFIG        = months.reduce((s, m) => s + m.earnFIG,        0) / count;
      const avgEarnFedEx      = months.reduce((s, m) => s + m.earnFedEx,      0) / count;
      const avgEarnOther      = months.reduce((s, m) => s + m.earnOther,      0) / count;
      const avgEarnCommission = months.reduce((s, m) => s + m.earnCommission, 0) / count;
      const avgEarn           = avgEarnFIG + avgEarnFedEx + avgEarnOther + avgEarnCommission;
      const avgSpend          = months.reduce((s, m) => s + m.spend, 0) / count;
      const avgNet            = months.reduce((s, m) => s + m.net,   0) / count;
      const label             = count < 3
        ? `Q${q} '${year.slice(2)} (${count} of 3 mo)`
        : `Q${q} '${year.slice(2)}`;
      return { label, avgEarnFIG, avgEarnFedEx, avgEarnOther, avgEarnCommission, avgEarn, avgSpend, avgNet };
    });
  const maxQtrVal = qtrScaleMode === 'linear'
    ? Math.max(...quarterlyTrend.flatMap(d => [d.avgEarn, d.avgSpend]), 1)
    : percentile(quarterlyTrend.flatMap(d => [d.avgEarn, d.avgSpend]), 0.75);
  const qSvgW = Q_PAD_L * 2 + quarterlyTrend.length * Q_SLOT;

  // ── Accounts header ───────────────────────────────────────────────────────────
  // Runway does not follow the tab or month controls, and it looks forward:
  // each coming month costs variable spend plus the recurring items active in
  // it, and the cash lasts until that adds up to more than there is.
  //   • Spend is always the consolidated All spend — every context except
  //     Trust, self-payroll eliminated, payroll taxes and ADP fees kept — over
  //     the trailing full months.
  //   • Variable spend is that, less the part matched to a recurring item.
  // No income is assumed.
  const latest = balances ? latestBalances(balances) : null;
  const liquid = liquidCash(latest);
  const pendingNow = pendingByMask(transactions);
  const today  = localDay(now);
  const recurringMatch = recurring ? matchRecurring(transactions, recurring.items, maskByAccountId(recurring.accounts)) : null;
  const variableInfo   = recurringMatch
    ? variableSpend(transactions, t => t.Context !== 'Trust' && t.Direction === 'Out' && !isExcludedGroup(t, true), recurringMatch.matchedIds, now)
    : null;
  const rw   = recurring ? forwardRunway(liquid, variableInfo ? variableInfo.variable : null, recurring.items, today) : null;
  const rent = recurring ? housingOutlook(recurring.items, today) : null;
  // Runway's spend side comes from Finance_Transactions: until they arrive it
  // shows a placeholder, never a figure computed from no rows.
  const runwayText = loading ? '…'
    : !rw || rw.months === null ? '—'
    : rw.capped ? `${RUNWAY_MAX_MONTHS}+ months`
    : `${rw.months.toFixed(1)} months`;
  const runwayNote = rent && 'Forward: variable spend + scheduled bills'
    + [rent.prepaidThrough && `rent prepaid through ${fmtDayShort(rent.prepaidThrough)}`, rent.newRentFrom && `new rent from ${fmtDayShort(rent.newRentFrom)}`]
      .filter(Boolean).map((part, i) => (i === 0 ? `; ${part}` : `, ${part}`)).join('')
    + '.';

  // Cash tiles show Available (what can be spent, pending already netted out)
  // and, when the account has pending rows, a small pending line under it.
  // Investment tiles show Current (market value).
  function acctTile(mask, mod = '', { cash = false } = {}) {
    const row = latest.byMask[mask];
    const p = cash ? pendingNow[mask] : null;
    return (
      <div key={mask} className={`fin-acct ${mod}`}>
        <div className="fin-acct-name">{row ? accountName(row) : 'No balance'}</div>
        <div className="fin-acct-mask">····{mask}</div>
        <div className="fin-acct-value">{fmtBal(cash ? cashBalance(row) : row?.Current)}</div>
        {p && (
          <div className="fin-acct-pending" title={`${p.count} pending`}>
            <span className="fin-pending-pill" aria-hidden="true" />
            Pending {p.net >= 0 ? '−' : '+'}{fmtBal(Math.abs(p.net))}
          </div>
        )}
      </div>
    );
  }

  // ── Context toggle button style ───────────────────────────────────────────────
  function ctxBtn(ctx) {
    const active = selectedContext === ctx;
    return {
      padding: '0.38rem 0.88rem', border: 'none', cursor: 'pointer',
      borderRadius: 4, fontWeight: 600, fontSize: '0.82rem',
      background: active ? '#4B0082' : '#faf7ee',
      color:      active ? '#fff'    : '#4B0082',
    };
  }

  function monthBtn(m) {
    const active = selectedMonth === m;
    return {
      padding: '0.38rem 0.88rem', border: 'none', cursor: 'pointer',
      borderRadius: 4, fontWeight: 600, fontSize: '0.82rem',
      background: active ? '#B8860B' : '#faf7ee',
      color:      active ? '#fff'    : '#B8860B',
    };
  }

  // No page-wide wait: the accounts header renders when Finance_Balances
  // lands and the Net Worth tab when its own lists do. Only what is built from
  // Finance_Transactions (Runway's figure, the spend sections, Recurring)
  // waits on them, each with its own loading state.

  // ── Render ────────────────────────────────────────────────────────────────────
  if (receiptsView) {
    const groceries = Object.entries(categoryGroupMap).find(([, c]) => /^groceries$/i.test(c?.name || ''));
    return <ReceiptsView start={receiptsView} onExit={() => setReceiptsView(null)} groceriesCategoryId={groceries ? Number(groceries[0]) : null} />;
  }
  return (
    <div className="fin-root">

      {/* ── Accounts header: runway + latest balances ── */}
      <div className="fin-accts">
        {balancesError ? (
          <div className="fin-accts-state fin-accts-state--error">
            <span>⚠ Balances unavailable — {balancesError}</span>
            <button className="fin-retry" onClick={fetchBalances}>Retry</button>
          </div>
        ) : balances === null ? (
          <div className="fin-accts-state">Loading balances…</div>
        ) : !latest ? (
          <div className="fin-accts-state">No balances recorded yet.</div>
        ) : (
          <>
            <div className="fin-accts-row fin-accts-row--cash">
              <div className="fin-acct fin-acct--runway">
                <div className="fin-acct-name">Runway</div>
                <div className="fin-acct-runway">{runwayText}</div>
                <div className="fin-acct-sub">
                  {error ? 'transactions unavailable'
                    : loading ? 'loading spend…'
                    : recurringError ? 'recurring bills unavailable'
                    : !rw ? 'loading bills…'
                    : <>variable {rw.variable !== null ? fmtBal(rw.variable) : '—'} + bills {fmtBal(rw.bills)}/mo</>}
                  {' '}· liquid {fmtBal(liquid)}
                </div>
              </div>
              {CASH_MASKS.map(mask => acctTile(mask, '', { cash: true }))}
            </div>
            <div className="fin-accts-row fin-accts-row--other">
              {INVEST_MASKS.map(mask => acctTile(mask, 'fin-acct--muted'))}
              <div className="fin-acct fin-acct--trust">
                <div className="fin-acct-name">{latest.trust ? accountName(latest.trust) : 'Trust'}</div>
                <div className="fin-acct-mask">{latest.trust?.Mask ? `····${latest.trust.Mask}` : ''}</div>
                <div className="fin-acct-value">{fmtBal(latest.trust?.Current)}</div>
                <div className="fin-acct-tag">client funds</div>
              </div>
            </div>
            <div className="fin-accts-asof">as of {fmtDay(latest.asOf)}</div>
            {runwayNote && <div className="fin-accts-note">{runwayNote}</div>}
          </>
        )}
      </div>

      {/* ── Controls ── */}
      <div className="fin-controls">
        <div className="fin-control-row">
          {CONTEXTS.map(ctx => (
            <button key={ctx} style={ctxBtn(ctx)} onClick={() => setSelectedContext(ctx)}>
              {CONTEXT_LABELS[ctx] || ctx}
            </button>
          ))}
          <button style={ctxBtn(RECURRING)} onClick={() => setSelectedContext(RECURRING)}>{RECURRING}</button>
          <span className="fin-tab-sep" aria-hidden="true" />
          <button style={ctxBtn(NET_WORTH)} onClick={() => setSelectedContext(NET_WORTH)}>{NET_WORTH}</button>
          <span className="fin-tab-sep" aria-hidden="true" />
          <button className="rc-link-btn" onClick={() => setReceiptsView({ kind: 'dash' })}>What we bought →</button>
          <button className="rc-link-btn" onClick={() => setReceiptsView({ kind: 'all' })}>All receipts →</button>
        </div>
        {!netWorthView && !recurringView && (
          <div className="fin-control-row">
            {allMonths.map(m => (
              <button key={m} style={monthBtn(m)} onClick={() => setSelectedMonth(m)}>
                {fmtMonth(m)}
              </button>
            ))}
          </div>
        )}
      </div>

      {netWorthView ? (
        netWorthError || balancesError ? (
          <div className="fin-state fin-state--error">
            <p>⚠ Net worth unavailable — {netWorthError || balancesError}</p>
            <button className="fin-retry" onClick={netWorthError ? fetchNetWorth : fetchBalances}>Retry</button>
          </div>
        ) : netWorthData === null || balances === null ? (
          <div className="fin-state">
            <div className="fin-spinner" />
            <p>Loading net worth…</p>
          </div>
        ) : (
          <NetWorthTab balances={balances} {...netWorthData} />
        )
      ) : recurringView ? (
        recurringError ? (
          <div className="fin-state fin-state--error">
            <p>⚠ Recurring unavailable — {recurringError}</p>
            <button className="fin-retry" onClick={fetchRecurring}>Retry</button>
          </div>
        ) : error ? (
          <div className="fin-state fin-state--error">
            <p>⚠ Recurring unavailable — {error}</p>
            <button className="fin-retry" onClick={fetchData}>Retry</button>
          </div>
        ) : loading || !recurringMatch ? (
          <div className="fin-state">
            <div className="fin-spinner" />
            <p>Loading recurring items…</p>
          </div>
        ) : (
          <RecurringTab items={recurring.items} match={recurringMatch} categories={categoryGroupMap} today={today} />
        )
      ) : error ? (
        <div className="fin-state fin-state--error">
          <p>⚠ {error}</p>
          <button className="fin-retry" onClick={fetchData}>Retry</button>
        </div>
      ) : loading ? (
        <div className="fin-state">
          <div className="fin-spinner" />
          <p>Loading transactions…</p>
        </div>
      ) : (
      <>

      {/* ── MAKE / SPEND / NET ── */}
      <div className="fin-summary">
        <div className="fin-card">
          <div className="fin-card-label">MAKE</div>
          <div className="fin-card-value fin-card-value--in">{fmt(realIn)}</div>
          <div className="fin-card-sub">real inflow</div>
        </div>
        <div className="fin-card">
          <div className="fin-card-label">SPEND</div>
          <div className="fin-card-value fin-card-value--out">{fmt(realOut)}</div>
          <div className="fin-card-sub">real outflow</div>
        </div>
        <div className="fin-card fin-card--net">
          <div className="fin-card-label">NET</div>
          <div
            className="fin-card-value"
            style={{ color: net >= 0 ? '#2e7d32' : '#c62828' }}
          >
            {net >= 0 ? '+' : '−'}{fmt(Math.abs(net))}
          </div>
          <div className="fin-card-sub">{net >= 0 ? 'ahead' : 'behind'}</div>
        </div>
      </div>

      {/* ── Excluded-group footnotes ── */}
      {movTotal > 0 && (
        <div className="fin-movement-note">
          <span className="fin-movement-badge">Movement</span>
          {fmt(movTotal)} excluded above — {consolidatedView
            ? 'transfers, owner draws, credit card payments & self-payroll'
            : 'transfers, owner draws & credit card payments'}
        </div>
      )}
      {assetsTotal > 0 && (
        <div className="fin-movement-note">
          <span className="fin-movement-badge" style={{ background: '#64748b' }}>Assets &amp; Savings</span>
          {fmt(assetsTotal)} excluded above — retirement, major purchases &amp; savings
        </div>
      )}

      {/* ── SPEND BY CATEGORY ── */}
      <div className="fin-section">
        <h3 className="fin-section-title">Spend by Category</h3>
        {sortedCats.length === 0 ? (
          <p className="fin-empty">No spending data for this period.</p>
        ) : (
          <div className="fin-chart-wrap" style={{ overflowX: 'auto' }}>
            {selectedMonth && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ width: LABEL_W, flexShrink: 0 }} />
                <div style={{ width: BAR_MAX_W, flexShrink: 0 }} />
                <div style={{ width: AMT_W, flexShrink: 0 }} />
                <div style={{ display: 'flex', gap: 4, paddingLeft: 6 }}>
                  <span style={{ width: 64, textAlign: 'right', fontSize: 10, color: '#9a8a7a', fontFamily: 'system-ui,sans-serif' }}>vs same pd</span>
                  <span style={{ width: 64, textAlign: 'right', fontSize: 10, color: '#9a8a7a', fontFamily: 'system-ui,sans-serif' }}>vs last mo</span>
                  <span style={{ width: 64, textAlign: 'right', fontSize: 10, color: '#9a8a7a', fontFamily: 'system-ui,sans-serif' }}>vs qtr avg</span>
                </div>
              </div>
            )}
            {sortedCats.map(([cat, total], i) => {
              const t      = i / Math.max(sortedCats.length - 1, 1);
              const fill   = lerpColor('#4B0082', '#c8a8e0', t);
              const barW   = Math.min(Math.max((Math.abs(total) / maxCat) * BAR_MAX_W, 2), BAR_MAX_W);
              const label  = cat.length > 24 ? cat.slice(0, 23) + '…' : cat;
              const isOpen = expandedCat === cat;

              let merchants = [];
              if (isOpen) {
                const mTotals = {};
                for (const tx of filtered) {
                  if (spendOk(tx) && catName(tx) === cat) {
                    // Pending charges get their own line, so the pill marks only them.
                    const pending = isPending(tx);
                    const key = `${pending ? 'p:' : ''}${normalizeMerchant(tx.Title).toLowerCase()}`;
                    if (!mTotals[key]) mTotals[key] = { key, display: normalizeMerchant(tx.Title), total: 0, pending, txs: [] };
                    mTotals[key].total += tx.Amount ?? 0;
                    mTotals[key].txs.push(tx);
                  }
                }
                merchants = Object.values(mTotals).sort((a, b) => b.total - a.total);
              }

              return (
                <div key={cat} style={{ marginBottom: BAR_GAP }}>
                  <div
                    onClick={() => setExpandedCat(c => c === cat ? null : cat)}
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ width: LABEL_W, flexShrink: 0, textAlign: 'right', paddingRight: 7,
                      fontSize: 12.5, color: '#2a1a0a', fontFamily: 'system-ui,sans-serif' }}>
                      {label}
                    </div>
                    <div style={{ width: BAR_MAX_W, flexShrink: 0, overflow: 'hidden' }}>
                      <div style={{ width: barW, height: BAR_H, borderRadius: 3, background: fill }} />
                    </div>
                    <div style={{ width: AMT_W, flexShrink: 0, paddingLeft: 7, fontSize: 12, color: '#5a3a10',
                      fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' }}>
                      {fmt(total)}{isOpen ? ' ▲' : ' ▼'}
                    </div>
                    {selectedMonth && (
                      <div style={{ display: 'flex', gap: 4, paddingLeft: 6 }}>
                        <span style={{ width: 64, textAlign: 'right', fontSize: 11 }}>{samePeriodCatTotals ? fmtSamePeriod(total, samePeriodCatTotals[cat]) : ''}</span>
                        <span style={{ width: 64, textAlign: 'right', fontSize: 11 }}>{fmtSamePeriod(total, prevMonthCatTotals[cat])}</span>
                        <span style={{ width: 64, textAlign: 'right', fontSize: 11 }}>{fmtDeltaNoArrow(total, qtrAvgCatTotals[cat])}</span>
                      </div>
                    )}
                  </div>
                  {isOpen && (
                    <div style={{
                      marginLeft: LABEL_W, marginTop: 3,
                      background: '#f5f0e8', borderRadius: 4, padding: '5px 10px',
                      maxWidth: BAR_MAX_W + AMT_W, boxSizing: 'border-box',
                    }}>
                      {merchants.length === 0
                        ? <div style={{ fontSize: 11, color: '#7a6a5a' }}>No transactions.</div>
                        : merchants.map(({ key, display, total: mTotal, pending, txs }) => (
                          <div key={key} className={pending ? 'fin-merchant fin-merchant--pending' : 'fin-merchant'} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                            fontSize: 11.5, color: pending ? '#9a8a7a' : '#2a1a0a', fontFamily: 'system-ui,sans-serif',
                            padding: '3px 0', borderBottom: '1px solid #e8dfc8',
                          }}>
                            <span style={{ marginRight: 12 }}>
                              {pending && <span className="fin-pending-pill" title="Pending" aria-label="pending" />}
                              {display}
                              <ReceiptChip txs={txs} categoryName={cat} onOpen={(id) => setReceiptsView({ kind: 'receipt', id })} />
                            </span>
                            <span className="fin-merchant-amt" style={{ fontWeight: pending ? 500 : 600, color: pending ? '#a89a8a' : '#5a3a10', whiteSpace: 'nowrap' }}>{fmt(mTotal)}</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── MONTHLY EARN vs SPEND ── */}
      <div className="fin-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
          <h3 className="fin-section-title" style={{ margin: 0 }}>Monthly — Earn vs Spend (excl. Movement)</h3>
          <button
            onClick={() => setMonthlyScaleMode(m => m === 'linear' ? 'fit' : 'linear')}
            style={{
              padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4, cursor: 'pointer',
              background: monthlyScaleMode === 'fit' ? '#4B0082' : '#faf7ee',
              color:      monthlyScaleMode === 'fit' ? '#fff'    : '#5a4a3a',
              fontWeight: 600, fontSize: '0.7rem',
            }}
          >{monthlyScaleMode === 'linear' ? 'Linear' : 'Fit'}</button>
          <button
            onClick={() => setMonthlyWindowStart(Math.max(0, windowStart - M_PAGE))}
            disabled={windowStart === 0}
            style={{
              padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4,
              cursor: windowStart === 0 ? 'default' : 'pointer',
              background: '#faf7ee', color: windowStart === 0 ? '#c4b89a' : '#5a4a3a',
              fontWeight: 700, fontSize: '0.7rem',
            }}
          >◀</button>
          {visibleMonths.length > 0 && (
            <span style={{ fontSize: '0.7rem', color: '#5a4a3a', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {fmtMonth(visibleMonths[0].month)} – {fmtMonth(visibleMonths[visibleMonths.length - 1].month)}
            </span>
          )}
          <button
            onClick={() => setMonthlyWindowStart(Math.min(maxStart, windowStart + M_PAGE))}
            disabled={windowStart === maxStart}
            style={{
              padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4,
              cursor: windowStart === maxStart ? 'default' : 'pointer',
              background: '#faf7ee', color: windowStart === maxStart ? '#c4b89a' : '#5a4a3a',
              fontWeight: 700, fontSize: '0.7rem',
            }}
          >▶</button>
        </div>
        {allMonths.length === 0 ? (
          <p className="fin-empty">No data.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#5a4a3a', flexWrap: 'wrap' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#4B0082', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />FIG</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#2e7d32', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />FedEx</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#B8860B', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Other</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#0d9488', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Commission</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#dc2626', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Spend</span>
            </div>
            <div className="fin-chart-wrap">
              <svg width={mSvgW} height={M_SVG_H} viewBox={`0 0 ${mSvgW} ${M_SVG_H}`} aria-label="Monthly earn vs spend">
                {visibleMonths.map(({ month, earnFIG, earnFedEx, earnOther, earnCommission, earn, spend, net }, i) => {
                  const earnRawH     = earn  > 0 ? (earn  / maxMonthlyVal) * M_H : 0;
                  const spendRawH    = spend > 0 ? (spend / maxMonthlyVal) * M_H : 0;
                  const earnClipped  = earnRawH  > M_H;
                  const spendClipped = spendRawH > M_H;
                  const earnBarH     = Math.min(earnRawH,  M_H);
                  const spendBarH    = Math.min(spendRawH, M_H);
                  const figH        = earn > 0 ? (earnFIG        / earn) * earnBarH : 0;
                  const fedexH      = earn > 0 ? (earnFedEx      / earn) * earnBarH : 0;
                  const otherH      = earn > 0 ? (earnOther      / earn) * earnBarH : 0;
                  const commissionH = earn > 0 ? (earnCommission / earn) * earnBarH : 0;
                  const earnX     = M_PAD_L + i * M_SLOT;
                  const spendX    = earnX + M_BAR + M_INNER_GAP;
                  const pairCtrX  = earnX + M_PAIR_W / 2;
                  const earnBarY  = M_PAD_T + M_H - earnBarH;
                  const spendBarY = M_PAD_T + M_H - spendBarH;
                  const baseY     = M_PAD_T + M_H;
                  const netStr    = net >= 0 ? '+' + fmt(net) : '−' + fmt(net);
                  const netColor  = net >= 0 ? '#2e7d32' : '#c62828';
                  return (
                    <g key={month}>
                      {/* Stacked earn: FIG (bottom) → FedEx → Other → Commission (top) */}
                      {earn === 0
                        ? <rect x={earnX} y={baseY - 1} width={M_BAR} height={1} fill="#ccc" />
                        : <>
                            {figH        > 0 && <rect x={earnX} y={baseY - figH}                                       width={M_BAR} height={figH}        fill="#4B0082" />}
                            {fedexH      > 0 && <rect x={earnX} y={baseY - figH - fedexH}                              width={M_BAR} height={fedexH}      fill="#2e7d32" />}
                            {otherH      > 0 && <rect x={earnX} y={baseY - figH - fedexH - otherH}                     width={M_BAR} height={otherH}      fill="#B8860B" />}
                            {commissionH > 0 && <rect x={earnX} y={baseY - figH - fedexH - otherH - commissionH}       width={M_BAR} height={commissionH} fill="#0d9488" />}
                          </>
                      }
                      {/* Spend bar */}
                      <rect x={spendX} y={spendBarY} width={M_BAR} height={Math.max(spendBarH, 1)} rx={2} fill="#dc2626" opacity={spendBarH > 0 ? 1 : 0.15} />
                      {/* Total earn label above stack */}
                      {earn > 0 && (
                        <text x={earnX + M_BAR / 2} y={Math.max(earnBarY - 4, 10)} textAnchor="middle"
                          fontSize="9" fill="#2a1a0a" fontFamily="system-ui,sans-serif" fontWeight="600">
                          {earnClipped ? fmt(earn) + ' ↑' : fmt(earn)}
                        </text>
                      )}
                      {/* Total spend label above bar */}
                      {spend > 0 && (
                        <text x={spendX + M_BAR / 2} y={Math.max(spendBarY - 4, 10)} textAnchor="middle"
                          fontSize="9" fill="#dc2626" fontFamily="system-ui,sans-serif" fontWeight="600">
                          {spendClipped ? fmt(spend) + ' ↑' : fmt(spend)}
                        </text>
                      )}
                      <text x={pairCtrX} y={baseY + 14} textAnchor="middle"
                        fontSize="11" fill="#5a4a3a" fontFamily="system-ui,sans-serif">
                        {fmtMonth(month)}
                      </text>
                      <text x={pairCtrX} y={baseY + 30} textAnchor="middle"
                        fontSize="10" fill={netColor} fontFamily="system-ui,sans-serif" fontWeight="700">
                        {netStr}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </>
        )}
      </div>

      {/* ── QUARTERLY AVERAGE — EARN vs SPEND ── */}
      <div className="fin-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
          <h3 className="fin-section-title" style={{ margin: 0 }}>Quarterly Average — Earn vs Spend</h3>
          <button
            onClick={() => setQtrScaleMode(m => m === 'linear' ? 'fit' : 'linear')}
            style={{
              padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4, cursor: 'pointer',
              background: qtrScaleMode === 'fit' ? '#4B0082' : '#faf7ee',
              color:      qtrScaleMode === 'fit' ? '#fff'    : '#5a4a3a',
              fontWeight: 600, fontSize: '0.7rem',
            }}
          >{qtrScaleMode === 'linear' ? 'Linear' : 'Fit'}</button>
        </div>
        {quarterlyTrend.length === 0 ? (
          <p className="fin-empty">No data.</p>
        ) : (
          <div className="fin-chart-wrap">
            <svg width={qSvgW} height={Q_SVG_H} viewBox={`0 0 ${qSvgW} ${Q_SVG_H}`} aria-label="Quarterly average earn vs spend">
              {quarterlyTrend.map(({ label, avgEarnFIG, avgEarnFedEx, avgEarnOther, avgEarnCommission, avgEarn, avgSpend, avgNet }, i) => {
                const earnRawH     = avgEarn  > 0 ? (avgEarn  / maxQtrVal) * Q_H : 0;
                const spendRawH    = avgSpend > 0 ? (avgSpend / maxQtrVal) * Q_H : 0;
                const earnClipped  = earnRawH  > Q_H;
                const spendClipped = spendRawH > Q_H;
                const earnBarH     = Math.min(earnRawH,  Q_H);
                const spendBarH    = Math.min(spendRawH, Q_H);
                const figH        = avgEarn > 0 ? (avgEarnFIG        / avgEarn) * earnBarH : 0;
                const fedexH      = avgEarn > 0 ? (avgEarnFedEx      / avgEarn) * earnBarH : 0;
                const otherH      = avgEarn > 0 ? (avgEarnOther      / avgEarn) * earnBarH : 0;
                const commissionH = avgEarn > 0 ? (avgEarnCommission / avgEarn) * earnBarH : 0;
                const earnX     = Q_PAD_L + i * Q_SLOT;
                const spendX    = earnX + Q_BAR + Q_INNER_GAP;
                const pairCtrX  = earnX + Q_PAIR_W / 2;
                const earnBarY  = Q_PAD_T + Q_H - earnBarH;
                const spendBarY = Q_PAD_T + Q_H - spendBarH;
                const baseY     = Q_PAD_T + Q_H;
                const netStr    = avgNet >= 0 ? '+' + fmt(avgNet) : '−' + fmt(avgNet);
                const netColor  = avgNet >= 0 ? '#2e7d32' : '#c62828';
                return (
                  <g key={label}>
                    {/* Stacked earn: FIG (bottom) → FedEx → Other → Commission (top) */}
                    {avgEarn === 0
                      ? <rect x={earnX} y={baseY - 1} width={Q_BAR} height={1} fill="#ccc" />
                      : <>
                          {figH        > 0 && <rect x={earnX} y={baseY - figH}                                       width={Q_BAR} height={figH}        fill="#4B0082" />}
                          {fedexH      > 0 && <rect x={earnX} y={baseY - figH - fedexH}                              width={Q_BAR} height={fedexH}      fill="#2e7d32" />}
                          {otherH      > 0 && <rect x={earnX} y={baseY - figH - fedexH - otherH}                     width={Q_BAR} height={otherH}      fill="#B8860B" />}
                          {commissionH > 0 && <rect x={earnX} y={baseY - figH - fedexH - otherH - commissionH}       width={Q_BAR} height={commissionH} fill="#0d9488" />}
                        </>
                    }
                    {/* Spend bar */}
                    <rect x={spendX} y={spendBarY} width={Q_BAR} height={Math.max(spendBarH, 1)} rx={3} fill="#dc2626" opacity={spendBarH > 0 ? 1 : 0.15} />
                    {/* Total earn label */}
                    {avgEarn > 0 && (
                      <text x={earnX + Q_BAR / 2} y={Math.max(earnBarY - 4, 10)} textAnchor="middle"
                        fontSize="10" fill="#2a1a0a" fontFamily="system-ui,sans-serif" fontWeight="600">
                        {earnClipped ? fmt(avgEarn) + ' ↑' : fmt(avgEarn)}
                      </text>
                    )}
                    {/* Total spend label */}
                    {avgSpend > 0 && (
                      <text x={spendX + Q_BAR / 2} y={Math.max(spendBarY - 4, 10)} textAnchor="middle"
                        fontSize="10" fill="#dc2626" fontFamily="system-ui,sans-serif" fontWeight="600">
                        {spendClipped ? fmt(avgSpend) + ' ↑' : fmt(avgSpend)}
                      </text>
                    )}
                    <text x={pairCtrX} y={baseY + 14} textAnchor="middle"
                      fontSize="10" fill="#5a4a3a" fontFamily="system-ui,sans-serif">
                      {label}
                    </text>
                    <text x={pairCtrX} y={baseY + 30} textAnchor="middle"
                      fontSize="10" fill={netColor} fontFamily="system-ui,sans-serif" fontWeight="700">
                      {netStr}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>

      </>
      )}

    </div>
  );
}
